import { Children, useEffect, useState } from 'react';
import TableBody, { type TableBodyProps } from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';

export const DEFAULT_PAGE_SIZE = 25;

/**
 * A table body that pages itself: up to a page of rows it is a plain `TableBody`; beyond that it shows one page at a time with
 * a pager under the rows (25, 50, 100 or all). Imported in place of MUI's `TableBody`, so a table pages without changing its rows.
 * Tables the server already pages, and statements read whole (trial balance, financial statements, schedules), keep MUI's.
 */
export default function PagedTableBody({ children, pageSize = DEFAULT_PAGE_SIZE, ...rest }: TableBodyProps & { pageSize?: number }) {
  const rows = Children.toArray(children);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(pageSize);
  const count = rows.length;
  const lastPage = size < 0 ? 0 : Math.max(0, Math.ceil(count / size) - 1);
  // A list that shrinks (a filter, a deletion) keeps the page within it.
  useEffect(() => { if (page > lastPage) setPage(lastPage); }, [page, lastPage]);
  if (count <= pageSize) return <TableBody {...rest}>{children}</TableBody>;
  const current = Math.min(page, lastPage);
  return (
    <TableBody {...rest}>
      {size < 0 ? rows : rows.slice(current * size, current * size + size)}
      <TableRow>
        <TableCell colSpan={100} sx={{ p: 0, borderBottom: 'none' }}>
          <TablePagination component="div" count={count} page={current} rowsPerPage={size} rowsPerPageOptions={[25, 50, 100, { label: 'All', value: -1 }]}
            onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }} />
        </TableCell>
      </TableRow>
    </TableBody>
  );
}
